/**
 * Op wat voor apparaat draait de app?
 *
 * De API kent zeven platforms en leidt daar de levensduur van het token uit af:
 * een velddevice (`ios`, `ipados`, `android`) krijgt 14 dagen, al het andere
 * 12 uur. Een verkeerd herkende iPad kost de inspecteur dus elke ochtend zijn
 * wachtwoord - daarom staat de herkenning hier los, met tests per userAgent.
 */
export const DEVICE_PLATFORMS = [
    'ios',
    'ipados',
    'android',
    'web',
    'windows',
    'macos',
    'other',
] as const;

export type DevicePlatform = (typeof DEVICE_PLATFORMS)[number];

/** Wat we van `navigator` gebruiken; los te geven in tests. */
export interface NavigatorLike {
    userAgent?: string;
    maxTouchPoints?: number;
}

const FIELD_PLATFORMS: readonly DevicePlatform[] = ['ios', 'ipados', 'android'];

/** Zoals de API ze in de apparatenlijst noemt. */
const PLATFORM_NAMES: Record<DevicePlatform, string | null> = {
    ios: 'iPhone',
    ipados: 'iPad',
    android: 'Android',
    web: null,
    windows: 'Windows',
    macos: 'macOS',
    other: null,
};

function currentNavigator(): NavigatorLike | undefined {
    return typeof navigator === 'undefined' ? undefined : navigator;
}

export function detectPlatform(
    nav: NavigatorLike | undefined = currentNavigator(),
): DevicePlatform {
    const userAgent = nav?.userAgent ?? '';

    if (userAgent === '') {
        return 'other';
    }

    if (/iPad/.test(userAgent)) {
        return 'ipados';
    }

    if (/iPhone|iPod/.test(userAgent)) {
        return 'ios';
    }

    if (/Android/.test(userAgent)) {
        return 'android';
    }

    /*
     * Sinds iPadOS 13 vraagt Safari op een iPad standaard de desktopsite en
     * noemt hij zich "Macintosh". Een Mac heeft geen touchscreen; een iPad meldt
     * vijf aanraakpunten. Dat verschil is de enige betrouwbare aanwijzing.
     */
    if (/Macintosh|Mac OS X/.test(userAgent)) {
        return (nav?.maxTouchPoints ?? 0) > 1 ? 'ipados' : 'macos';
    }

    if (/Windows/.test(userAgent)) {
        return 'windows';
    }

    return 'web';
}

/** Een velddevice krijgt 14 dagen, de rest 12 uur. */
export function isFieldPlatform(platform: DevicePlatform): boolean {
    return FIELD_PLATFORMS.includes(platform);
}

function detectBrowser(userAgent: string): string | null {
    const browsers: Array<[RegExp, string]> = [
        [/EdgiOS|EdgA|Edg\//, 'Edge'],
        [/OPR\/|Opera/, 'Opera'],
        [/SamsungBrowser/, 'Samsung Internet'],
        [/Firefox|FxiOS/, 'Firefox'],
        [/CriOS|Chrome\//, 'Chrome'],
        [/Safari\//, 'Safari'],
    ];

    return browsers.find(([pattern]) => pattern.test(userAgent))?.[1] ?? null;
}

/**
 * De naam in "Apparaten met toegang": browser en apparaat, zodat iemand zijn
 * eigen tablet herkent ("Safari op iPad"). Hoogstens 120 tekens.
 */
export function describeDevice(nav: NavigatorLike | undefined = currentNavigator()): string {
    const platform = detectPlatform(nav);
    const browser = detectBrowser(nav?.userAgent ?? '') ?? 'Browser';
    const device = PLATFORM_NAMES[platform];

    return (device ? `${browser} op ${device}` : browser).slice(0, 120);
}
